"use client";

import { useEffect, useRef } from "react";

import { openDepositModal } from "@/components/modal/deposit-modal";
import { useAnalytics } from "@/contexts/analytics-context";

import { useFunDepositOverride } from "./fun-state-override";

const MODAL_SELECTOR = '[data-modal-id="deposit"]';
const RETRY_MS = 150;
const MAX_ATTEMPTS = 60;

/**
 * Opens the deposit modal when `?funState=` is on the URL, and does nothing at all otherwise.
 *
 * The card pane lives behind the modal's own Deposit button, which is gated on a delegated
 * session — a gate an automated run has no cheap way through. This mounts the modal on its
 * normal form step instead, so the run still reaches the pane by pressing the real Card option.
 *
 * Retried rather than fired once: `openModal` is a window event, so a dispatch that lands before
 * `ModalContainer` has subscribed is dropped with no trace, and how long that takes moves with
 * whatever else the page is hydrating.
 */
export function FunDepositOverrideHarness() {
  const override = useFunDepositOverride();
  const analytics = useAnalytics();
  // Once it has opened, it stays closed if the run closes it — reopening behind a test that just
  // dismissed the modal to look at what is underneath would be its own bug.
  const opened = useRef(false);

  useEffect(() => {
    if (!override || opened.current) return;
    let attempts = 0;
    let timer = 0;

    const tick = () => {
      if (document.querySelector(MODAL_SELECTOR)) {
        opened.current = true;
        return;
      }
      if (attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      openDepositModal(analytics);
      timer = window.setTimeout(tick, RETRY_MS);
    };
    tick();

    return () => window.clearTimeout(timer);
  }, [override, analytics]);

  return null;
}
